import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { User, AuthTokens, ClinicListItem } from '@/types'

interface AuthState {
  user: User | null
  accessToken: string | null
  refreshToken: string | null
  isAuthenticated: boolean
  activeClinicId: string | null
  clinicsList: ClinicListItem[]
  
  // Actions
  setUser: (user: User) => void
  setTokens: (accessToken: string, refreshToken: string) => void
  login: (tokens: AuthTokens & { user: User, active_clinic_id?: string, clinics?: ClinicListItem[] }) => void
  logout: () => void
  updateUser: (updates: Partial<User>) => void
  switchClinic: (clinicId: string, tokens: AuthTokens) => void
  setClinicsList: (clinics: ClinicListItem[]) => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      activeClinicId: null,
      clinicsList: [],
      
      setUser: (user) => set({ user }),
      
      setTokens: (accessToken, refreshToken) => 
        set({ accessToken, refreshToken }),
      
      login: ({ access_token, refresh_token, user, active_clinic_id, clinics }) =>
        set({
          user,
          accessToken: access_token,
          refreshToken: refresh_token,
          isAuthenticated: true,
          activeClinicId: active_clinic_id || null,
          clinicsList: clinics || [],
        }),
      
      logout: () =>
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
          activeClinicId: null,
          clinicsList: [],
        }),
      
      updateUser: (updates) =>
        set((state) => ({
          user: state.user ? { ...state.user, ...updates } : null,
        })),

      switchClinic: (clinicId, tokens) =>
        set({
          activeClinicId: clinicId,
          accessToken: tokens.access_token,
          refreshToken: tokens.refresh_token,
        }),

      setClinicsList: (clinics) => set({ clinicsList: clinics }),
    }),
    {
      name: 'hms-auth-storage',
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
        activeClinicId: state.activeClinicId,
        clinicsList: state.clinicsList,
      }),
    }
  )
)
